import type { ProgressRequest } from './apiContracts';
import type { PrototypeRuntime } from './prototypeTypes';

type MediaProgressSnapshot = Omit<ProgressRequest, 'chapterId'>;
type PendingProgress = {
	snapshot: MediaProgressSnapshot;
	resolveChapterId: () => Promise<string | null>;
	retryCount: number;
};

/**
 * Coalesces media snapshots for one runtime/chapter.  A cursor is a mutable
 * value: replaying or seeking backwards must let the latest value win rather
 * than allowing an earlier in-flight request to be replayed afterwards.
 */
class LatestOnlyProgressExecutor {
	private inFlight = false;
	private latest: PendingProgress | null = null;

	constructor(private readonly runtime: PrototypeRuntime) {}

	enqueue(snapshot: MediaProgressSnapshot, resolveChapterId: () => Promise<string | null>): void {
		// The caller may keep mutating its runtime/media object after this call.
		// Keep an immutable request value so an old await cannot report a newer
		// chapter or cursor under its own ordering key.
		this.latest = { snapshot: structuredClone(snapshot), resolveChapterId, retryCount: 0 };
		if (!this.inFlight) void this.drain();
	}

	private async drain(): Promise<void> {
		this.inFlight = true;
		try {
			while (this.latest) {
				const pending = this.latest;
				this.latest = null;
				try {
					const chapterId = await pending.resolveChapterId();
					// A cache miss may have allowed a newer cursor to arrive. Do not
					// enqueue the older snapshot after that lookup completes.
					if (this.latest) continue;
					if (!chapterId) continue;
					await this.runtime.api.updateProgress({ ...pending.snapshot, chapterId });
				} catch {
					// Retry the newest failed value once. A subsequently observed cursor
					// replaces it, so an old position can never leapfrog newer playback.
					if (!this.latest && pending.retryCount < 1) this.latest = { ...pending, retryCount: pending.retryCount + 1 };
				}
			}
		} finally {
			this.inFlight = false;
			if (this.latest) void this.drain();
		}
	}
}

const executors = new WeakMap<PrototypeRuntime, Map<string, LatestOnlyProgressExecutor>>();

export function enqueueLatestMediaProgress(
	runtime: PrototypeRuntime,
	chapterKey: string,
	snapshot: MediaProgressSnapshot,
	resolveChapterId: () => Promise<string | null>,
): void {
	let byChapter = executors.get(runtime);
	if (!byChapter) {
		byChapter = new Map();
		executors.set(runtime, byChapter);
	}
	let executor = byChapter.get(chapterKey);
	if (!executor) {
		executor = new LatestOnlyProgressExecutor(runtime);
		byChapter.set(chapterKey, executor);
	}
	executor.enqueue(snapshot, resolveChapterId);
}
